/**
 * theme.ts — Single source of truth for the GenWizard light design system.
 *
 * Usage:
 *   import { T, cls, injectTheme, injectLandingTheme } from '../theme/theme';
 *
 *   // Inject base theme (cards, buttons, tabs, etc.):
 *   injectTheme();
 *
 *   // Additionally inject landing-page styles:
 *   injectLandingTheme();
 *
 *   // JSX:
 *   <div className={cls('t-card')}>...</div>
 *
 *   // Inline colour tokens:
 *   <span style={{ color: T.brand }}>...</span>
 */

export const ACCENTURE_LOGO_SRC =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MCA0MS41Ij48bGluZWFyR3JhZGllbnQgaWQ9ImEiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIiB4MT0iLjE1IiB5MT0iMzYuOTA3IiB4Mj0iMzkuOTk1IiB5Mj0iMjAuNzQyIj48c3RvcCBvZmZzZXQ9IjAiIHN0b3AtY29sb3I9IiNhMTAwZmYiLz48c3RvcCBvZmZzZXQ9Ii4xNzQiIHN0b3AtY29sb3I9IiM5NTAwZWQiLz48c3RvcCBvZmZzZXQ9Ii41MjYiIHN0b3AtY29sb3I9IiM3NjAwYmQiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM0NjAwNzMiLz48L2xpbmVhckdyYWRpZW50PjxwYXRoIGZpbGw9InVybCgjYSkiIGQ9Ik00MCAxNi4yTDAgMzIuNHY5LjFsNDAtMTYuMnoiLz48cGF0aCBmaWxsPSIjYTEwMGZmIiBkPSJNNDAgMjUuM0wwIDkuMVYwbDQwIDE2LjJ6Ii8+PC9zdmc+';


// ─── Palette ──────────────────────────────────────────────────────────────────
export const T = {
  bg:           '#f4f4f7',
  bgCard:       '#ffffff',
  bgSidebar:    '#f7f7f9',
  textPrimary:  '#0f1923',
  textSecond:   '#1e2d3d',
  textMuted:    '#445566',
  textLabel:    '#2c3e52',
  brand:        '#460073',
  brandLight:   '#f3e8ff',
  brandMid:     '#c9a7f0',
  brandDark:    '#2e004d',
  border:       '#dcdce3',
  green:        '#166534',
  greenBg:      '#f0fdf4',
  greenBorder:  '#86efac',
  amber:        '#7c2d00',
  amberBg:      '#fff7ed',
  amberBorder:  '#fde68a',
  red:          '#991b1b',
  redBg:        '#fef2f2',
  redBorder:    '#fca5a5',
  purple:       '#6b21a8',
  purpleBg:     '#faf5ff',
  purpleBorder: '#d8b4fe',
  slate:        '#334155',
  slateBg:      '#f8fafc',
  slateBorder:  '#cbd5e1',
  teal:         '#0f766e',
  tealBg:       '#f0fdfa',
  // Used for SAP system badges, SEMANTIC match-type, "info" accents.
  blue:         '#1d4ed8',
  blueBg:       '#eff6ff',
  blueBorder:   '#93c5fd',
  tealBorder:   '#5eead4',

  background:   '#f4f4f7',          // alias for bg
  grey:         '#334155',          // alias for slate
  greyBg:       '#f8fafc',          // alias for slateBg
  greyBorder:   '#cbd5e1',          // alias for slateBorder
  borderLight:  '#dbe2eb',          // slightly lighter variant of border
  textStrong:   ''
} as const;

// ─── Bar-chart palette ────────────────────────────────────────────────────────
export const BAR_PALETTE = [
  T.brand, T.brandMid, T.green, '#86efac',
  T.purple, '#d8b4fe', T.amber, '#fde68a',
  T.slate, '#94a3b8', '#0f766e', '#99f6e4',
] as const;

// Token type → semantic chip color map. Used by both editor and dashboard.
export const TOKEN_TYPE_PALETTE: Record<string, { color: string; bg: string; border: string }> = {
  legal_suffix:   { color: T.purple,    bg: T.purpleBg,    border: T.purpleBorder },
  country_code:   { color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  currency_code:  { color: T.green,     bg: T.greenBg,     border: T.greenBorder },
  payment_terms:  { color: T.amber,     bg: T.amberBg,     border: T.amberBorder },
  phone_number:   { color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  email_address:  { color: T.purple,    bg: T.purpleBg,    border: T.purpleBorder },
  postal_code:    { color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  street_address: { color: T.amber,     bg: T.amberBg,     border: T.amberBorder },
  address_type:   { color: T.green,     bg: T.greenBg,     border: T.greenBorder },
  status_enum:    { color: T.purple,    bg: T.purpleBg,    border: T.purpleBorder },
  tax_id:         { color: T.red,       bg: T.redBg,       border: T.redBorder },
  duns_number:    { color: T.red,       bg: T.redBg,       border: T.redBorder },
  supplier_name:  { color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  generic:        { color: T.textMuted, bg: T.bgSidebar,   border: T.border },
};

// Source of a token block — also semantic
export const SOURCE_PALETTE: Record<string, { color: string; bg: string }> = {
  llm:    { color: T.brand,  bg: T.brandLight },
  sme:    { color: T.green,  bg: T.greenBg },
  merged: { color: T.purple, bg: T.purpleBg },
  policy: { color: T.amber,  bg: T.amberBg },
};

// ─── className helper ─────────────────────────────────────────────────────────
export function cls(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

// ─── Google Fonts snippet (shared) ───────────────────────────────────────────
const GW_FONTS = `@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=JetBrains+Mono:wght@400;600&family=Sora:wght@300;400;600;700&display=swap');`;

// ═════════════════════════════════════════════════════════════════════════════
//  BASE THEME CSS  (cards, buttons, forms, tabs, sidebar, etc.)
// ═════════════════════════════════════════════════════════════════════════════
const THEME_CSS_ID = 'gw-theme-css';

export const THEME_CSS = `
${GW_FONTS}

.gw-root, :root {
  --gw-bg:           ${T.bg};
  --gw-bg-card:      ${T.bgCard};
  --gw-bg-sidebar:   ${T.bgSidebar};
  --gw-text:         ${T.textPrimary};
  --gw-text-second:  ${T.textSecond};
  --gw-text-muted:   ${T.textMuted};
  --gw-text-label:   ${T.textLabel};
  --gw-brand:        ${T.brand};
  --gw-brand-light:  ${T.brandLight};
  --gw-brand-mid:    ${T.brandMid};
  --gw-brand-dark:   ${T.brandDark};
  --gw-border:       ${T.border};
  --gw-green:        ${T.green};
  --gw-green-bg:     ${T.greenBg};
  --gw-green-bd:     ${T.greenBorder};
  --gw-amber:        ${T.amber};
  --gw-amber-bg:     ${T.amberBg};
  --gw-amber-bd:     ${T.amberBorder};
  --gw-red:          ${T.red};
  --gw-red-bg:       ${T.redBg};
  --gw-red-bd:       ${T.redBorder};
  --gw-purple:       ${T.purple};
  --gw-purple-bg:    ${T.purpleBg};
  --gw-purple-bd:    ${T.purpleBorder};
  --gw-slate:        ${T.slate};
  --gw-slate-bg:     ${T.slateBg};
  --gw-slate-bd:     ${T.slateBorder};
}

.gw-root {
  background: var(--gw-bg);
  color: var(--gw-text);
  font-family: 'Sora', system-ui, sans-serif;
}

/* ── Typography ── */
.gw-mono   { font-family: 'JetBrains Mono', monospace; }
.gw-serif  { font-family: 'DM Serif Display', Georgia, serif; }
.gw-label  {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 600;
  letter-spacing: .14em; text-transform: uppercase;
  color: var(--gw-text-muted);
}
.gw-section-hdr {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 600;
  letter-spacing: .16em; text-transform: uppercase;
  color: var(--gw-text-muted);
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 14px;
}
.gw-section-hdr::after { content:''; flex:1; height:1px; background:var(--gw-border); }

/* ── Cards ── */
.gw-card { background:var(--gw-bg-card); border:1px solid var(--gw-border); border-radius:12px; overflow:hidden; }
.gw-card-hdr {
  padding:11px 16px; border-bottom:1px solid var(--gw-border);
  background:var(--gw-bg-sidebar);
  display:flex; align-items:center; justify-content:space-between;
}
.gw-card-title { font-size:13px; font-weight:700; color:var(--gw-text); }
.gw-card-body  { padding:16px; }

/* ── Tabs ── */
.gw-tabs {
  display:flex; gap:4px; padding:8px 12px;
  background:var(--gw-bg-card); border-bottom:1px solid var(--gw-border);
  overflow-x:auto; flex-wrap:nowrap; scrollbar-width:none;
}
.gw-tabs::-webkit-scrollbar { display:none; }
.gw-tab {
  display:inline-flex; align-items:center; gap:5px;
  padding:6px 13px; border-radius:8px; font-size:12px; font-weight:600;
  cursor:pointer; border:none; background:transparent;
  color:var(--gw-text-muted); white-space:nowrap;
  font-family:'Sora', system-ui, sans-serif;
  transition:background .13s, color .13s;
}
.gw-tab:hover  { background:var(--gw-brand-light); color:var(--gw-brand); }
.gw-tab.active { background:var(--gw-brand); color:#fff; }
.gw-tab-icon   { font-size:12px; }

/* ── Form elements ── */
.gw-form-grid   { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:16px; }
.gw-form-grid-1 { grid-template-columns:1fr; }
.gw-form-grid-3 { grid-template-columns:1fr 1fr 1fr; }
.gw-form-group  { display:flex; flex-direction:column; gap:5px; }
.gw-input, .gw-select, .gw-textarea {
  padding:8px 11px; border-radius:8px;
  border:1px solid var(--gw-border);
  background:var(--gw-bg-sidebar);
  font-size:13px; color:var(--gw-text);
  font-family:'Sora', system-ui, sans-serif;
  outline:none; width:100%;
  transition:border-color .13s, background .13s, box-shadow .13s;
}
.gw-input:focus, .gw-select:focus, .gw-textarea:focus {
  border-color:var(--gw-brand-mid);
  background:var(--gw-bg-card);
  box-shadow:0 0 0 3px rgba(30,58,110,.07);
}
.gw-textarea { resize:vertical; min-height:72px; }
.gw-select {
  appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23445566'/%3E%3C/svg%3E");
  background-repeat:no-repeat;
  background-position:right 10px center;
  background-color:var(--gw-bg-sidebar);
  padding-right:28px;
}
.gw-checkbox-row   { display:flex; align-items:center; gap:8px; margin-bottom:4px; }
.gw-checkbox       { width:16px; height:16px; border-radius:4px; accent-color:var(--gw-brand); cursor:pointer; }
.gw-checkbox-label { font-size:13px; font-weight:500; color:var(--gw-text); }

/* ── Buttons ── */
.gw-btn {
  display:inline-flex; align-items:center; gap:6px;
  padding:7px 16px; border-radius:8px; font-size:12px; font-weight:700;
  cursor:pointer; border:1px solid transparent;
  font-family:'Sora', system-ui, sans-serif;
  transition:background .13s, border-color .13s, color .13s, transform .1s;
  line-height:1;
}
.gw-btn:active { transform:scale(.98); }
.gw-btn-sm     { padding:5px 12px; font-size:11px; }
.gw-btn-icon   { width:30px; height:30px; padding:0; border-radius:7px; justify-content:center; }
.gw-btn-primary   { background:var(--gw-brand);    color:#fff; border-color:var(--gw-brand); }
.gw-btn-primary:hover { background:var(--gw-brand-dark); }
.gw-btn-outline   { background:var(--gw-bg-card);  color:var(--gw-brand); border-color:var(--gw-brand-mid); }
.gw-btn-outline:hover { background:var(--gw-brand-light); }
.gw-btn-ghost     { background:var(--gw-bg-sidebar); color:var(--gw-text-muted); border-color:var(--gw-border); }
.gw-btn-ghost:hover   { border-color:var(--gw-slate); color:var(--gw-text-second); }
.gw-btn-danger    { background:var(--gw-red-bg); color:var(--gw-red); border-color:var(--gw-red-bd); }
.gw-btn-danger:hover  { background:var(--gw-red); color:#fff; }
.gw-btn-success   { background:var(--gw-green-bg); color:var(--gw-green); border-color:var(--gw-green-bd); }
.gw-btn-success:hover { background:var(--gw-green); color:#fff; }

/* ── Badges ── */
.gw-badge {
  font-family:'JetBrains Mono', monospace; font-size:9px; font-weight:700;
  padding:2px 8px; border-radius:8px; letter-spacing:.05em;
  display:inline-flex; align-items:center; white-space:nowrap;
}
.gw-badge-brand  { background:var(--gw-brand-light); color:var(--gw-brand);  border:1px solid var(--gw-brand-mid); }
.gw-badge-green  { background:var(--gw-green-bg);    color:var(--gw-green);  border:1px solid var(--gw-green-bd); }
.gw-badge-amber  { background:var(--gw-amber-bg);    color:var(--gw-amber);  border:1px solid var(--gw-amber-bd); }
.gw-badge-red    { background:var(--gw-red-bg);      color:var(--gw-red);    border:1px solid var(--gw-red-bd); }
.gw-badge-purple { background:var(--gw-purple-bg);   color:var(--gw-purple); border:1px solid var(--gw-purple-bd); }
.gw-badge-muted  { background:var(--gw-bg-sidebar);  color:var(--gw-text-muted); border:1px solid var(--gw-border); }

/* ── Table ── */
.gw-table { width:100%; border-collapse:collapse; font-size:13px; }
.gw-table thead th {
  font-family:'JetBrains Mono', monospace; font-size:9px; font-weight:600;
  letter-spacing:.12em; text-transform:uppercase; color:var(--gw-text-muted);
  padding:10px 14px; background:var(--gw-bg-sidebar);
  border-bottom:1.5px solid var(--gw-border); text-align:left;
}
.gw-table tbody tr   { border-bottom:1px solid var(--gw-border); }
.gw-table tbody tr:last-child { border-bottom:none; }
.gw-table tbody tr:hover td  { background:rgba(30,58,110,.025); }
.gw-table tbody td   { padding:10px 14px; vertical-align:middle; }

/* ── Sidebar / Tree ── */
.gw-sidebar {
  background:var(--gw-bg-sidebar); border-right:1px solid var(--gw-border);
  display:flex; flex-direction:column; overflow:hidden;
}
.gw-sidebar-hdr {
  padding:10px 14px 9px; border-bottom:1px solid var(--gw-border);
  display:flex; align-items:center; justify-content:space-between;
}
.gw-sidebar-hdr-title {
  font-family:'JetBrains Mono', monospace; font-size:9px; font-weight:600;
  letter-spacing:.14em; text-transform:uppercase; color:var(--gw-text-muted);
}
.gw-sidebar-scroll {
  flex:1; overflow-y:auto; padding:8px 6px;
}
.gw-sidebar-scroll::-webkit-scrollbar { width:4px; }
.gw-sidebar-scroll::-webkit-scrollbar-thumb { background:var(--gw-border); border-radius:2px; }
.gw-sidebar-footer {
  padding:9px 11px; border-top:1px solid var(--gw-border);
  display:flex; gap:6px; background:var(--gw-bg-card);
}

/* ── Tree nodes ── */
.gw-node { margin-bottom:2px; }
.gw-node-row {
  display:flex; align-items:center; gap:5px;
  padding:6px 8px; border-radius:8px; cursor:pointer;
  transition:background .12s;
}
.gw-node-row:hover     { background:var(--gw-brand-light); }
.gw-node-row.selected  { background:var(--gw-brand-light); box-shadow:inset 3px 0 0 var(--gw-brand); }
.gw-node-label         { font-size:12px; font-weight:600; color:var(--gw-text); flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.gw-node-label.group   { color:var(--gw-brand); font-size:11px; font-weight:700; }
.gw-node-meta          { font-size:10px; color:var(--gw-text-muted); }
.gw-node-children      { padding-left:18px; }
.gw-drag-handle        { color:var(--gw-border); font-size:9px; cursor:grab; flex-shrink:0; }

/* ── KPI chips ── */
.gw-kpi-strip { display:flex; flex-wrap:wrap; gap:3px; padding:3px 8px 7px 26px; }
.gw-kpi-chip {
  display:inline-flex; align-items:center;
  font-family:'JetBrains Mono', monospace; font-size:8px; font-weight:600;
  padding:2px 7px; border-radius:9px; cursor:pointer;
  transition:all .12s; border:1px solid transparent;
}
.gw-kpi-cols   { background:var(--gw-bg-sidebar);  color:var(--gw-text-muted); border-color:var(--gw-border); }
.gw-kpi-null   { background:var(--gw-red-bg);       color:var(--gw-red);        border-color:var(--gw-red-bd); }
.gw-kpi-null.active   { background:var(--gw-red);    color:#fff; }
.gw-kpi-single { background:var(--gw-amber-bg);     color:var(--gw-amber);      border-color:var(--gw-amber-bd); }
.gw-kpi-single.active { background:var(--gw-amber);  color:#fff; }
.gw-kpi-two    { background:var(--gw-green-bg);     color:var(--gw-green);      border-color:var(--gw-green-bd); }
.gw-kpi-two.active    { background:var(--gw-green);  color:#fff; }
.gw-kpi-multi  { background:var(--gw-purple-bg);    color:var(--gw-purple);     border-color:var(--gw-purple-bd); }
.gw-kpi-multi.active  { background:var(--gw-purple); color:#fff; }
.gw-kpi-cavg   { background:var(--gw-brand-light);  color:var(--gw-brand);      border-color:var(--gw-brand-mid); }

/* ── Filter pills ── */
.gw-filter-pill {
  display:inline-flex; align-items:center; gap:5px;
  padding:4px 10px; border-radius:14px; font-size:11px; font-weight:600;
  cursor:pointer; border:1px solid var(--gw-border);
  background:var(--gw-bg-card); color:var(--gw-text-muted);
  transition:all .13s; white-space:nowrap;
  font-family:'Sora', system-ui, sans-serif;
}
.gw-filter-pill:hover        { border-color:var(--gw-brand-mid); color:var(--gw-brand); background:var(--gw-brand-light); }
.gw-filter-pill.active       { background:var(--gw-brand); border-color:var(--gw-brand); color:#fff; }
.gw-pill-count {
  font-size:10px; font-weight:700;
  background:var(--gw-bg-sidebar); color:var(--gw-text-muted);
  border-radius:8px; padding:0 5px;
}
.gw-filter-pill.active .gw-pill-count { background:rgba(255,255,255,.2); color:#fff; }

/* ── Modal ── */
.gw-modal-backdrop {
  position:fixed; inset:0; background:rgba(10,20,40,.35);
  backdrop-filter:blur(2px); z-index:1050;
  display:flex; align-items:center; justify-content:center;
}
.gw-modal {
  background:var(--gw-bg-card); border-radius:14px;
  border:1px solid var(--gw-border);
  box-shadow:0 20px 60px rgba(10,20,40,.18);
  width:680px; max-width:95vw; max-height:88vh;
  display:flex; flex-direction:column; overflow:hidden;
}
.gw-modal-hdr {
  padding:15px 20px; border-bottom:1px solid var(--gw-border);
  background:var(--gw-bg-sidebar);
  display:flex; align-items:center; justify-content:space-between;
}
.gw-modal-title { font-size:15px; font-weight:700; color:var(--gw-text); }
.gw-modal-body  { flex:1; overflow-y:auto; padding:20px; }
.gw-modal-body::-webkit-scrollbar { width:4px; }
.gw-modal-body::-webkit-scrollbar-thumb { background:var(--gw-border); border-radius:2px; }
.gw-modal-foot  {
  padding:11px 20px; border-top:1px solid var(--gw-border);
  background:var(--gw-bg-sidebar);
  display:flex; justify-content:flex-end; gap:8px;
}
.gw-close-btn {
  width:28px; height:28px; border-radius:6px;
  border:none; background:none; cursor:pointer;
  color:var(--gw-text-muted); font-size:18px;
  display:flex; align-items:center; justify-content:center;
  transition:color .12s;
}
.gw-close-btn:hover { color:var(--gw-red); }

.gw-field-hdr {
  padding: 13px 16px 11px;
  background: var(--gw-bg-card);
  border-bottom: 1px solid var(--gw-border);
  display: flex; align-items: center;
  justify-content: space-between; gap: 12px;
}

.gw-field-hdr-name {
  font-size: 16px; font-weight: 700;
  color: var(--gw-text);
}
  
.gw-field-hdr-meta {
  display: flex; gap: 7px; 
  align-items: center; margin-top: 3px;
  flex-wrap: wrap;
}

/* ── Match-field row ── */
.gw-mf-row {
  background:var(--gw-bg-sidebar); border:1px solid var(--gw-border);
  border-radius:10px; padding:12px 14px; margin-bottom:8px;
  display:grid; grid-template-columns:1fr auto auto auto;
  gap:12px; align-items:end;
}
.gw-mf-row:last-child { margin-bottom:0; }

/* ── Confidence bar ── */
.gw-conf-bar  { height:4px; background:var(--gw-border); border-radius:2px; overflow:hidden; display:inline-block; vertical-align:middle; }
.gw-conf-fill { height:100%; background:var(--gw-brand); border-radius:2px; }

/* ── Chart card ── */
.gw-chart-card { background:var(--gw-bg-card); border:1px solid var(--gw-border); border-radius:12px; overflow:hidden; }
.gw-chart-hdr  { padding:10px 16px; border-bottom:1px solid var(--gw-border); background:var(--gw-bg-sidebar); display:flex; align-items:center; justify-content:space-between; }
.gw-chart-title {
  font-family:'JetBrains Mono', monospace; font-size:9px; font-weight:600;
  letter-spacing:.14em; text-transform:uppercase; color:var(--gw-text-muted);
}
.gw-chart-body { padding:14px 16px; }

/* ── Toolbar ── */
.gw-toolbar {
  display:flex; align-items:center; justify-content:space-between;
  padding:9px 16px; background:var(--gw-bg-card);
  border-bottom:1px solid var(--gw-border);
  gap:10px; flex-wrap:wrap;
}
.gw-toolbar-left  { display:flex; align-items:center; gap:8px; flex-wrap:wrap; flex:1; }
.gw-toolbar-right { flex-shrink:0; display:flex; gap:8px; align-items:center; }

/* ── Scrollbar reset ── */
.gw-scroll::-webkit-scrollbar       { width:4px; height:4px; }
.gw-scroll::-webkit-scrollbar-thumb { background:var(--gw-border); border-radius:2px; }
.gw-scroll { scrollbar-width:thin; scrollbar-color:var(--gw-border) transparent; }

/* ── React-Select overrides ── */
.gw-select-wrap .select__control { border-radius:8px !important; border:1px solid var(--gw-border) !important; background:var(--gw-bg-sidebar) !important; min-height:36px !important; box-shadow:none !important; }
.gw-select-wrap .select__control:hover { border-color:var(--gw-brand-mid) !important; }
.gw-select-wrap .select__control--is-focused { border-color:var(--gw-brand-mid) !important; box-shadow:0 0 0 3px rgba(30,58,110,.07) !important; }
.gw-select-wrap .select__menu { border-radius:8px !important; border:1px solid var(--gw-border) !important; box-shadow:0 8px 24px rgba(0,0,0,.1) !important; z-index:9999 !important; }
.gw-select-wrap .select__option--is-selected { background:var(--gw-brand) !important; }
.gw-select-wrap .select__option--is-focused:not(.select__option--is-selected) { background:var(--gw-brand-light) !important; color:var(--gw-brand) !important; }
.gw-select-wrap .select__multi-value { background:var(--gw-brand-light) !important; border-radius:6px !important; }
.gw-select-wrap .select__multi-value__label { color:var(--gw-brand) !important; font-size:11px !important; font-weight:600 !important; }
.gw-select-wrap .select__multi-value__remove:hover { background:var(--gw-brand) !important; color:#fff !important; }
.gw-select-wrap .select__placeholder { font-size:12px !important; color:var(--gw-text-muted) !important; }
.gw-select-wrap .select__input input { font-family:'Sora', system-ui, sans-serif !important; font-size:12px !important; }

/* ── Stacked / mini bars ── */
.gw-stack-bar { height:6px; border-radius:3px; overflow:hidden; display:flex; background:var(--gw-bg-sidebar); }
.gw-mini-bar  { height:4px; border-radius:2px; overflow:hidden; background:var(--gw-border); }

/* ── Animations ── */
.gw-rise    { opacity:0; transform:translateY(12px); animation:gwrise .48s forwards; }
.gw-rise-1  { animation-delay:.04s; }
.gw-rise-2  { animation-delay:.12s; }
.gw-rise-3  { animation-delay:.20s; }
.gw-rise-4  { animation-delay:.28s; }
@keyframes gwrise { to { opacity:1; transform:none; } }
.gw-dot-pulse   { animation:gwpulse 2.2s ease-in-out infinite; }
.gw-dot-pulse-2 { animation-delay:.4s; }
.gw-dot-pulse-3 { animation-delay:.8s; }
.gw-dot-pulse-4 { animation-delay:1.2s; }
@keyframes gwpulse { 0%,100%{opacity:1} 50%{opacity:.2} }

/* ── Empty state ── */
.gw-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 20px; color:var(--gw-text-muted); text-align:center; }
.gw-empty-icon  { font-size:36px; margin-bottom:12px; opacity:.3; }
.gw-empty-label { font-size:13px; font-weight:300; }

/* ── AppHeader (shared nav bar) ── */
.gw-app-header {
  /* Accenture template: black bar, purple ">" mark, core-purple accent line */
  /* the accent line is a background stripe, not a border: the bar stays exactly its height */
  background: #000000 linear-gradient(90deg, #460073 0%, #7500C0 50%, #A100FF 100%) no-repeat left bottom / 100% 3px;
  border-bottom: 0;
  flex: none;
  padding: 0 32px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 200;
  box-shadow: 0 2px 12px rgba(0,0,0,.25);
}
.gw-app-header-brand {
  display: flex;
  align-items: center;
  gap: 14px;
}
.gw-app-header-divider {
  width: 1px;
  height: 26px;
  background: #3a3a3a;
}
.gw-app-header-wordmark {
  font-family: Graphik, 'Helvetica Neue', Arial, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: .06em;
}
.gw-app-header-sub {
  font-size: 10px;
  color: #b3b3b3;
  font-weight: 300;
  letter-spacing: .04em;
}
.gw-app-header-pill {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .1em;
  color: ${T.brand};
  background: ${T.brandLight};
  border: 1px solid ${T.brandMid};
  border-radius: 20px;
  padding: 3px 10px;
}
.gw-app-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
/* contents of the black header: readable on black */
.gw-app-header-brand span { color: #e6e6e6 !important; }
.gw-app-header .gw-btn-ghost, .gw-app-header .gw-btn-outline { color: #ffffff; border-color: #5a5a5a; background: transparent; }
.gw-app-header .gw-btn-ghost:hover, .gw-app-header .gw-btn-outline:hover { border-color: #A100FF; color: #ffffff; }
.gw-app-header .gw-btn-primary { background: #A100FF; border-color: #A100FF; color: #ffffff; }
.gw-app-header .gw-btn-primary:hover { background: #7500C0; border-color: #7500C0; }
.gw-app-header .gw-user-menu-trigger { color: #ffffff; background: transparent; border-color: #5a5a5a; }
.gw-app-header .gw-user-avatar { background: #A100FF; color: #ffffff; }

/* ── AppSidebar (polished left nav) ── */
.gw-app-sidebar {
  background: ${T.bgCard};
  border-right: 1px solid ${T.border};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  user-select: none;
}
.gw-app-sidebar.collapsed { width: 56px; }
.gw-app-sidebar.expanded  { width: 220px; }

.gw-app-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 10px;
  border-bottom: 1px solid ${T.border};
  min-height: 44px;
}
.gw-app-sidebar-app-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: ${T.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gw-app-sidebar-toggle {
  width: 24px; height: 24px;
  border-radius: 6px;
  border: 1px solid ${T.border};
  background: ${T.bgSidebar};
  color: ${T.textMuted};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 11px;
  flex-shrink: 0;
  transition: background .13s, border-color .13s;
}
.gw-app-sidebar-toggle:hover { background: ${T.brandLight}; border-color: ${T.brandMid}; color: ${T.brand}; }

.gw-app-sidebar-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 8px;
  scrollbar-width: thin;
  scrollbar-color: ${T.border} transparent;
}
.gw-app-sidebar-scroll::-webkit-scrollbar       { width: 3px; }
.gw-app-sidebar-scroll::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 2px; }

.gw-app-sidebar-group-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${T.textMuted};
  padding: 10px 10px 4px;
  white-space: nowrap;
  overflow: hidden;
}

.gw-app-sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 2px;
  transition: background .12s, color .12s;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}
.gw-app-sidebar-item:hover {
  background: ${T.brandLight};
}
.gw-app-sidebar-item.active {
  background: ${T.brandLight};
  box-shadow: inset 3px 0 0 ${T.brand};
}
.gw-app-sidebar-item-icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  background: transparent;
  transition: background .12s;
}
.gw-app-sidebar-item.active .gw-app-sidebar-item-icon {
  background: ${T.brand};
  color: #fff;
}
.gw-app-sidebar-item:not(.active):hover .gw-app-sidebar-item-icon {
  background: ${T.brandLight};
}
.gw-app-sidebar-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}
.gw-app-sidebar-item-label {
  font-size: 12px;
  font-weight: 600;
  color: ${T.textSecond};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gw-app-sidebar-item.active .gw-app-sidebar-item-label {
  color: ${T.brand};
}
.gw-app-sidebar-item-sublabel {
  font-size: 10px;
  color: ${T.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}
.gw-app-sidebar-item-badge {
  margin-left: auto;
  font-family: 'JetBrains Mono', monospace;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
  background: ${T.brandLight};
  color: ${T.brand};
  border: 1px solid ${T.brandMid};
  white-space: nowrap;
  flex-shrink: 0;
}

.gw-app-sidebar-footer {
  padding: 10px 10px;
  border-top: 1px solid ${T.border};
  background: ${T.bgSidebar};
}
`;

// ═════════════════════════════════════════════════════════════════════════════
//  LANDING PAGE CSS
//  Shared prefix: .gwl- (landing) to avoid collisions with base .gw- classes.
// ═════════════════════════════════════════════════════════════════════════════
const LANDING_CSS_ID = 'gw-landing-css';

export const LANDING_CSS = `
/* ── landing root ── */
.gwl-root {
  min-height: 100vh;
  background: ${T.bg};
  color: ${T.textPrimary};
  font-family: 'Sora', system-ui, sans-serif;
  overflow-x: hidden;
}

/* ── hero ── */
.gwl-hero {
  max-width: 1100px;
  margin: 0 auto;
  padding: 72px 48px 56px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}
@media(max-width:860px){.gwl-hero{grid-template-columns:1fr;padding:48px 24px 32px;}}

.gwl-hero-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .22em;
  text-transform: uppercase;
  color: ${T.brand};
  background: ${T.brandLight};
  border: 1px solid ${T.brandMid};
  border-radius: 20px;
  padding: 9px 14px;
  display: inline-block;
  margin-bottom: 24px;
}
.gwl-hero-title {
  font-family: 'DM Serif Display', Georgia, serif;
  font-size: clamp(32px, 4vw, 52px);
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -.02em;
  color: ${T.textPrimary};
  margin: 0 0 18px;
}
.gwl-hero-title em { font-style: italic; color: ${T.brand}; }
.gwl-hero-desc {
  font-size: 15px;
  font-weight: 300;
  line-height: 1.75;
  color: ${T.textMuted};
  margin: 0;
  max-width: 440px;
}
.gwl-hero-right {
  background: ${T.bgCard};
  border: 1px solid ${T.border};
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 24px rgba(30,58,110,.07);
}
.gwl-feature-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid ${T.border};
}
.gwl-feature-item:last-child { border-bottom: none; }
.gwl-feature-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
}
.gwl-feature-title { font-size: 13px; font-weight: 700; color: ${T.textPrimary}; margin-bottom: 2px; }
.gwl-feature-desc  { font-size: 11px; font-weight: 300; color: ${T.textMuted}; line-height: 1.55; }

/* ── divider ── */
.gwl-divider {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 48px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
}
.gwl-divider-line {
  flex: 1;
  height: 1px;
  background: ${T.border};
}
.gwl-divider-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${T.textMuted};
  white-space: nowrap;
}

/* ── tile grid ── */
.gwl-tiles {
  margin: 0 auto;
  padding: 0 48px 72px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  max-width: 70%;
}
@media(max-width:768px){.gwl-tiles{grid-template-columns:1fr;padding:0 20px 48px;}}

/* ── tile ── */
.gwl-tile {
  background: ${T.bgCard};
  border: 1px solid ${T.border};
  border-radius: 14px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: border-color .2s, box-shadow .2s, transform .18s;
}
.gwl-tile::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  border-radius: 14px 14px 0 0;
  transition: opacity .2s;
}
.gwl-tile:hover { box-shadow: 0 8px 32px rgba(30,58,110,.1); transform: translateY(-2px); }

.gwl-tile.cyan::before   { background: linear-gradient(90deg, ${T.brand}, ${T.brandMid}); }
.gwl-tile.green::before  { background: linear-gradient(90deg, ${T.green}, #4ade80); }
.gwl-tile.amber::before  { background: linear-gradient(90deg, ${T.amber}, #f59e0b); }
.gwl-tile.purple::before { background: linear-gradient(90deg, ${T.purple}, #a78bfa); }
.gwl-tile.cyan:hover   { border-color: ${T.brandMid}; }
.gwl-tile.green:hover  { border-color: ${T.greenBorder}; }
.gwl-tile.amber:hover  { border-color: ${T.amberBorder}; }
.gwl-tile.purple:hover { border-color: ${T.purpleBorder}; }

.gwl-tile-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}
.gwl-tile-icon-wrap {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.gwl-tile-idx {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px; font-weight: 600;
  letter-spacing: .14em; text-transform: uppercase;
  color: ${T.textMuted};

  padding: 10px 8px;
}
.gwl-tile-title {
  font-family: 'DM Serif Display', Georgia, serif;
  font-size: 20px; font-weight: 400; line-height: 1.2;
  color: ${T.textPrimary};
  margin-bottom: 10px;
  letter-spacing: -.01em;
}
.gwl-tile-desc {
  font-size: 13px; font-weight: 300; line-height: 1.65;
  color: ${T.textMuted};
  flex: 1;
  margin-bottom: 24px;
}

/* ── CTA buttons ── */
.gwl-cta-stack { display: flex; flex-direction: column; gap: 8px; }
.gwl-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: 'Sora', system-ui, sans-serif;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all .15s;
  width: 100%;
  background: none;
  text-align: left;
}
.gwl-cta-arr { transition: transform .15s; font-size: 13px; }
.gwl-cta:hover .gwl-cta-arr { transform: translateX(4px); }

.gwl-cta.cyan-c   { background: ${T.brandLight}; border-color: ${T.brandMid};    color: ${T.brand}; }
.gwl-cta.cyan-c:hover  { background: #c8daf0; border-color: ${T.brand}; }
.gwl-cta.green-c  { background: ${T.greenBg};   border-color: ${T.greenBorder};  color: ${T.green}; }
.gwl-cta.green-c:hover { background: #dcfce7; border-color: #4ade80; }
.gwl-cta.amber-c  { background: ${T.amberBg};   border-color: ${T.amberBorder};  color: ${T.amber}; }
.gwl-cta.amber-c:hover { background: #fef3c7; border-color: #f59e0b; }
.gwl-cta.purple-c { background: ${T.purpleBg};  border-color: ${T.purpleBorder}; color: ${T.purple}; }
.gwl-cta.purple-c:hover{ background: #ede9fe; border-color: #a78bfa; }
.gwl-cta.ghost    { background: ${T.bgSidebar}; border-color: ${T.border};       color: ${T.textMuted}; }
.gwl-cta.ghost:hover   { border-color: ${T.slate}; color: ${T.textSecond}; }

/* ── Footer ── */
.gwl-footer {
  background: ${T.bgCard};
  border-top: 1px solid ${T.border};
  padding: 16px 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.gwl-footer-txt {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: ${T.textMuted};
  letter-spacing: .06em;
}

/* ── Animations ── */
.gwl-rise { opacity: 0; transform: translateY(14px); animation: gwrise .5s forwards; }
.gwl-rise:nth-child(1) { animation-delay: .04s; }
.gwl-rise:nth-child(2) { animation-delay: .12s; }
.gwl-rise:nth-child(3) { animation-delay: .20s; }
.gwl-rise:nth-child(4) { animation-delay: .28s; }
.gwl-hero-rise { opacity: 0; transform: translateY(12px); animation: gwrise .6s forwards; }
.gwl-hero-rise:nth-child(1) { animation-delay: .06s; }
.gwl-hero-rise:nth-child(2) { animation-delay: .16s; }
.gwl-hero-rise:nth-child(3) { animation-delay: .26s; }
.gwl-hero-rise:nth-child(4) { animation-delay: .36s; }

/* Locked-module tile treatment. Append to your landing theme.
 *
 * Intent: the tile is clearly still a product surface, but unmistakably not
 * actionable. Desaturate and dim rather than hide, and put the reason on the
 * card so nobody has to ask.
 */
.gwl-tile-locked {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
}

/* Desaturate and dim the real tile underneath, and kill all interaction. */
.gwl-tile-locked-inner {
  filter: grayscale(0.85) opacity(0.45);
  pointer-events: none;
  user-select: none;
}

.gwl-tile-locked-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background: linear-gradient(180deg,
              rgba(248, 250, 252, 0.55) 0%,
              rgba(248, 250, 252, 0.88) 55%);
  backdrop-filter: blur(1.5px);
}

.gwl-tile-locked-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.gwl-tile-locked-msg {
  margin: 0;
  max-width: 260px;
  color: #475569;
  font-size: 12.5px;
  line-height: 1.55;
}

.gwl-tile-locked-btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.gwl-tile-locked-btn:hover {
  border-color: #94a3b8;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
}

/* Respect a user's reduced-transparency / reduced-motion preference. */
@media (prefers-reduced-transparency: reduce) {
  .gwl-tile-locked-overlay { backdrop-filter: none; background: #f8fafc; }
}



// Add inside THEME_CSS string, after the last existing rule

/* ── User menu dropdown ── */
.gw-user-menu {
  position: relative;
  display: inline-flex;
}
.gw-user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid ${T.border};
  background: ${T.bgSidebar};
  cursor: pointer;
  font-family: 'Sora', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${T.textSecond};
  transition: background .13s, border-color .13s;
}
.gw-user-menu-trigger:hover {
  background: ${T.brandLight};
  border-color: ${T.brandMid};
  color: ${T.brand};
}
.gw-user-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${T.brand};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: 'JetBrains Mono', monospace;
}
.gw-user-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  background: ${T.bgCard};
  border: 1px solid ${T.border};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(30,58,110,.12);
  min-width: 220px;
  z-index: 9999;
  overflow: hidden;
}
.gw-user-dropdown-info {
  padding: 14px 16px;
  background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
}
.gw-user-dropdown-name {
  font-size: 13px;
  font-weight: 700;
  color: ${T.textPrimary};
  margin-bottom: 2px;
}
.gw-user-dropdown-email {
  font-size: 11px;
  color: ${T.textMuted};
  font-family: 'JetBrains Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gw-user-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: ${T.textSecond};
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-family: 'Sora', system-ui, sans-serif;
  transition: background .12s;
}
.gw-user-dropdown-item:hover {
  background: ${T.brandLight};
  color: ${T.brand};
}
.gw-user-dropdown-item.danger {
  color: ${T.red};
}
.gw-user-dropdown-item.danger:hover {
  background: ${T.redBg};
  color: ${T.red};
}
.gw-user-dropdown-divider {
  height: 1px;
  background: ${T.border};
  margin: 4px 0;
}
.gw-user-dropdown-item-icon {
  font-size: 14px;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}

/* ── Admin page ── */
.gw-admin-page {
  max-width: 900px;
  margin: 32px auto;
  padding: 0 24px 48px;
}
.gw-admin-page-title {
  font-family: 'DM Serif Display', Georgia, serif;
  font-size: 28px;
  font-weight: 400;
  color: ${T.textPrimary};
  margin: 0 0 4px;
}
.gw-admin-page-sub {
  font-size: 13px;
  color: ${T.textMuted};
  font-weight: 300;
  margin: 0 0 28px;
}
.gw-admin-stat-strip {
  display: flex;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.gw-admin-stat {
  background: ${T.bgCard};
  border: 1px solid ${T.border};
  border-radius: 10px;
  padding: 14px 20px;
  min-width: 130px;
  flex: 1;
}
.gw-admin-stat-val {
  font-family: 'JetBrains Mono', monospace;
  font-size: 24px;
  font-weight: 700;
  color: ${T.brand};
  line-height: 1;
  margin-bottom: 4px;
}
.gw-admin-stat-label {
  font-size: 11px;
  color: ${T.textMuted};
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: .08em;
}
.gw-admin-form-card {
  background: ${T.bgCard};
  border: 1px solid ${T.border};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}
.gw-admin-form-hdr {
  padding: 14px 20px;
  background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
  font-size: 13px;
  font-weight: 700;
  color: ${T.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
}
.gw-admin-form-body {
  padding: 20px;
}
.gw-admin-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.gw-admin-form-row-3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.gw-admin-success {
  background: ${T.greenBg};
  border: 1px solid ${T.greenBorder};
  border-radius: 8px;
  padding: 10px 14px;
  color: ${T.green};
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.gw-admin-error {
  background: ${T.redBg};
  border: 1px solid ${T.redBorder};
  border-radius: 8px;
  padding: 10px 14px;
  color: ${T.red};
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.gw-user-table {
  width: 100%;
  border-collapse: collapse;
}
.gw-user-table th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: ${T.textMuted};
  padding: 10px 14px;
  background: ${T.bgSidebar};
  border-bottom: 1.5px solid ${T.border};
  text-align: left;
  white-space: nowrap;
}
.gw-user-table td {
  padding: 11px 14px;
  border-bottom: 1px solid ${T.border};
  font-size: 13px;
  color: ${T.textPrimary};
  vertical-align: middle;
}
.gw-user-table tr:last-child td { border-bottom: none; }
.gw-user-table tr:hover td { background: rgba(30,58,110,.025); }
.gw-pwd-display {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  background: ${T.bgSidebar};
  border: 1px solid ${T.border};
  border-radius: 6px;
  padding: 8px 12px;
  color: ${T.brand};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}

/* ── Change password modal ── */
.gw-cpwd-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10,20,40,.35);
  backdrop-filter: blur(2px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.gw-cpwd-modal {
  background: ${T.bgCard};
  border: 1px solid ${T.border};
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(10,20,40,.18);
  width: 400px;
  max-width: 95vw;
  overflow: hidden;
}
.gw-cpwd-hdr {
  padding: 14px 20px;
  background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.gw-cpwd-title {
  font-size: 14px;
  font-weight: 700;
  color: ${T.textPrimary};
}
.gw-cpwd-body { padding: 20px; }
.gw-cpwd-foot {
  padding: 12px 20px;
  border-top: 1px solid ${T.border};
  background: ${T.bgSidebar};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}


/* ───────────────────────────────────────────────────────────────────────────
   Wizard / DataInput page — works in 3 contexts:
     1. standalone (full page)
     2. .gw-wizard-embedded (inside an existing panel/card)
     3. .gw-wizard-modal    (inside a Bootstrap Modal body)
   ─────────────────────────────────────────────────────────────────────────── */

.gw-wizard {
  display: flex;
  flex-direction: column;
  background: ${T.bg};
  min-height: 100vh;
  font-family: 'Sora', system-ui, sans-serif;
}

/* ── Header (standalone only) ── */
.gw-wizard-header {
  padding: 24px 36px 18px;
  background: ${T.bgCard};
  border-bottom: 1px solid ${T.border};
  flex-shrink: 0;
}
.gw-wizard-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${T.textMuted};
  margin-bottom: 6px;
}
.gw-wizard-title {
  font-family: 'DM Serif Display', Georgia, serif;
  font-size: 24px;
  font-weight: 400;
  letter-spacing: -.01em;
  color: ${T.textPrimary};
  margin: 0 0 18px;
}

/* ── Stepper ── */
.gw-stepper {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.gw-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px 7px 9px;
  border-radius: 99px;
  border: 1px solid ${T.border};
  background: ${T.bgCard};
  font-size: 12px;
  font-weight: 600;
  color: ${T.textMuted};
  cursor: pointer;
  transition: all .15s;
  font-family: 'Sora', system-ui, sans-serif;
}
.gw-step:hover {
  border-color: ${T.brandMid};
  color: ${T.brand};
  background: ${T.brandLight};
}
.gw-step.active {
  background: ${T.brand};
  border-color: ${T.brand};
  color: #fff;
}
.gw-step.done {
  background: ${T.greenBg};
  border-color: ${T.greenBorder};
  color: ${T.green};
}
.gw-step-num {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(0,0,0,.06);
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.gw-step.active .gw-step-num { background: rgba(255,255,255,.22); color: #fff; }
.gw-step.done   .gw-step-num { background: ${T.green}; color: #fff; }
.gw-step-arrow {
  font-size: 13px;
  color: ${T.border};
  margin: 0 2px;
}

/* ── Stepper-only header (embedded/modal) ── */
.gw-wizard-stepper-only {
  padding: 0 0 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${T.border};
  flex-shrink: 0;
}

/* ── Body ── */
.gw-wizard-body {
  flex: 1;
  padding: 28px 36px;
  overflow-y: auto;
  min-height: 0;
}
.gw-wizard-narrow {
  max-width: 880px;
  margin: 0 auto;
}

/* ── Section header ── */
.gw-wizard .gw-section-hdr {
  margin-top: 0;
  margin-bottom: 16px;
}

/* ── Source tab strip ── */
.gw-src-tabs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
  margin-bottom: 24px;
}
.gw-src-tab {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1.5px solid ${T.border};
  background: ${T.bgCard};
  cursor: pointer;
  color: ${T.textMuted};
  font-family: 'Sora', system-ui, sans-serif;
  text-align: left;
  transition: all .15s;
  min-height: 64px;
}
.gw-src-tab:hover {
  border-color: ${T.brandMid};
  background: ${T.brandLight};
  color: ${T.brand};
}
.gw-src-tab.active {
  border-color: ${T.brand};
  background: ${T.brandLight};
  color: ${T.brand};
  box-shadow: 0 2px 10px rgba(30,58,110,.08);
}
.gw-src-tab.disabled { opacity: .4; cursor: not-allowed; }
.gw-src-tab.disabled:hover {
  border-color: ${T.border};
  color: ${T.textMuted};
  background: ${T.bgCard};
}
.gw-src-tab-icon {
  font-size: 22px;
  line-height: 1;
  flex-shrink: 0;
}
.gw-src-tab-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.gw-src-tab-label {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}
.gw-src-tab-sub {
  font-size: 11px;
  font-weight: 400;
  color: ${T.textMuted};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gw-src-tab.active .gw-src-tab-sub { color: ${T.brand}; opacity: .8; }

/* ── Drop zone ── */
.gw-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${T.border};
  border-radius: 14px;
  background: ${T.bgSidebar};
  padding: 40px 24px;
  cursor: pointer;
  transition: all .15s;
  text-align: center;
  min-height: 160px;
}
.gw-dropzone:hover {
  border-color: ${T.brandMid};
  background: ${T.brandLight};
}
.gw-dropzone.has-files {
  border-style: solid;
  border-color: ${T.brandMid};
  background: ${T.brandLight};
  align-items: stretch;
  padding: 18px 22px;
  min-height: auto;
}
.gw-dropzone-icon {
  font-size: 34px;
  margin-bottom: 12px;
  opacity: .55;
}
.gw-dropzone-label {
  font-size: 14px;
  font-weight: 600;
  color: ${T.textSecond};
  margin-bottom: 4px;
}
.gw-dropzone-sub {
  font-size: 12px;
  color: ${T.textMuted};
}

/* ── Validation panel, sheet rows, badges (unchanged from before) ── */
.gw-validation-panel {
  margin-top: 18px;
  border: 1px solid ${T.border};
  border-radius: 10px;
  background: ${T.bgCard};
  overflow: hidden;
}
.gw-validation-hdr {
  padding: 12px 16px;
  border-bottom: 1px solid ${T.border};
  background: ${T.bgSidebar};
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: ${T.textSecond};
}
.gw-sheet-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid ${T.border};
  background: ${T.bgCard};
  transition: background .12s;
}
.gw-sheet-row:first-child { border-top: none; }
.gw-sheet-row.included { background: ${T.brandLight}; }
.gw-sheet-row.disabled { opacity: .55; }
.gw-sheet-checkbox {
  margin-top: 4px;
  accent-color: ${T.brand};
  width: 15px; height: 15px;
  cursor: pointer;
  flex-shrink: 0;
}
.gw-sheet-checkbox:disabled { cursor: not-allowed; }
.gw-sheet-info { flex: 1; min-width: 0; }
.gw-sheet-name-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-bottom: 4px;
}
.gw-sheet-name { font-size: 13px; font-weight: 700; color: ${T.textPrimary}; }
.gw-sheet-meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px; color: ${T.textMuted};
}
.gw-mode-badge {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  letter-spacing: .05em;
  padding: 2px 7px; border-radius: 6px;
  border: 1px solid;
}
.gw-mode-badge.enriched { background: ${T.greenBg};   color: ${T.green};  border-color: ${T.greenBorder}; }
.gw-mode-badge.typed    { background: ${T.amberBg};   color: ${T.amber};  border-color: ${T.amberBorder}; }
.gw-mode-badge.raw      { background: ${T.brandLight};color: ${T.brand};  border-color: ${T.brandMid}; }
.gw-mode-badge.empty    { background: ${T.redBg};     color: ${T.red};    border-color: ${T.redBorder}; }
.gw-mode-badge.rejected { background: ${T.redBg};     color: ${T.red};    border-color: ${T.redBorder}; }
.gw-sheet-reason { font-size: 11.5px; color: ${T.textMuted}; line-height: 1.5; margin-top: 2px; }
.gw-sheet-fields {
  display: flex; flex-direction: column; gap: 4px;
  margin-top: 8px; padding: 8px 11px;
  background: ${T.bgSidebar}; border-radius: 6px; font-size: 11px;
}
.gw-sheet-fields-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.gw-sheet-field-row { display: flex; align-items: center; gap: 6px; }
.gw-sheet-field-edit {
  width: 100%; padding: 5px 9px;
  border: 1px solid ${T.border}; border-radius: 5px;
  font-size: 11.5px; background: ${T.bgCard};
  color: ${T.textPrimary}; outline: none;
  font-family: 'Sora', system-ui, sans-serif;
}
.gw-sheet-field-edit:focus {
  border-color: ${T.brandMid};
  box-shadow: 0 0 0 3px rgba(30,58,110,.07);
}

/* ── Mode toggle ── */
.gw-mode-toggle {
  display: flex; gap: 4px; padding: 4px;
  border-radius: 9px;
  background: ${T.bgSidebar};
  border: 1px solid ${T.border};
  margin-bottom: 12px;
}
.gw-mode-toggle-btn {
  all: unset;
  flex: 1; text-align: center;
  padding: 9px 14px; border-radius: 6px;
  font-size: 12px; font-weight: 600;
  color: ${T.textMuted};
  cursor: pointer;
  transition: all .12s;
  font-family: 'Sora', system-ui, sans-serif;
}
.gw-mode-toggle-btn:hover { color: ${T.brand}; }
.gw-mode-toggle-btn.active {
  background: ${T.bgCard};
  color: ${T.brand};
  box-shadow: 0 1px 3px rgba(0,0,0,.06);
}
.gw-mode-explainer {
  font-size: 11.5px; color: ${T.textMuted};
  padding: 8px 12px; border-radius: 6px;
  background: ${T.bgSidebar};
  border: 1px dashed ${T.border};
  margin-bottom: 16px; line-height: 1.55;
}

/* ── DDL preview ── */
.gw-ddl-preview {
  border: 1px solid ${T.border}; border-radius: 10px;
  background: ${T.bgCard}; margin-top: 16px; overflow: hidden;
}
.gw-ddl-preview-hdr {
  padding: 11px 16px; background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; font-weight: 600; color: ${T.textSecond};
}
.gw-ddl-table { border-top: 1px solid ${T.border}; padding: 12px 16px; }
.gw-ddl-table:first-of-type { border-top: none; }
.gw-ddl-cols {
  display: grid; grid-template-columns: 1fr; gap: 4px;
  font-size: 11.5px; font-family: 'JetBrains Mono', monospace;
}
.gw-ddl-col-row {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 8px; border-radius: 4px;
}
.gw-ddl-col-row:hover { background: ${T.bgSidebar}; }
.gw-ddl-col-pk { color: ${T.amber}; font-weight: 700; width: 18px; text-align: center; }
.gw-ddl-col-null {
  margin-left: auto; font-size: 9.5px;
  letter-spacing: .04em; color: ${T.textMuted};
}
.gw-ddl-warn {
  padding: 10px 16px; background: ${T.amberBg};
  color: ${T.amber};
  border-top: 1px solid ${T.amberBorder};
  font-size: 11.5px;
}

/* ── DB / Catalog source cards ── */
.gw-source-card {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 20px;
  border: 1.5px solid ${T.border};
  border-radius: 10px;
  background: ${T.bgCard};
  cursor: pointer; transition: all .15s;
}
.gw-source-card:hover { border-color: ${T.brandMid}; background: ${T.brandLight}; }
.gw-source-card.connected { border-color: ${T.brand}; background: ${T.brandLight}; }
.gw-source-card-icon { font-size: 32px; flex-shrink: 0; }
.gw-source-card-body { flex: 1; min-width: 0; }
.gw-source-card-title { font-size: 14px; font-weight: 700; color: ${T.textPrimary}; }
.gw-source-card-sub {
  font-size: 12px; color: ${T.textMuted};
  margin-top: 3px; font-family: 'JetBrains Mono', monospace;
}
.gw-source-card-cta {
  font-size: 12px; font-weight: 600;
  color: ${T.brand}; background: ${T.bgCard};
  border: 1px solid ${T.brandMid};
  padding: 7px 14px; border-radius: 7px;
  flex-shrink: 0;
}

/* ── Relationship editor ── */
.gw-rel-editor {
  border: 1px solid ${T.border}; border-radius: 10px;
  background: ${T.bgCard}; overflow: hidden;
}
.gw-rel-hdr {
  padding: 12px 16px; background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
  display: flex; align-items: center; justify-content: space-between;
}
.gw-rel-hdr-title { font-size: 12px; font-weight: 700; color: ${T.textSecond}; }
.gw-rel-row {
  display: flex; align-items: center; gap: 6px;
  padding: 9px 16px; border-top: 1px solid ${T.border};
  font-size: 12px;
}
.gw-rel-row.suggested { background: ${T.amberBg}; }
.gw-rel-row.confirmed { background: ${T.greenBg}; }
.gw-rel-select {
  flex: 1; min-width: 0;
  padding: 6px 9px; border-radius: 6px;
  border: 1px solid ${T.border};
  background: ${T.bgCard};
  font-size: 12px; color: ${T.textPrimary};
  outline: none; font-family: 'Sora', system-ui, sans-serif;
}
.gw-rel-select:focus { border-color: ${T.brandMid}; box-shadow: 0 0 0 3px rgba(30,58,110,.07); }
.gw-rel-arrow { color: ${T.textMuted}; font-size: 14px; flex-shrink: 0; }
.gw-rel-conf {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px; font-weight: 700;
  padding: 2px 7px; border-radius: 4px;
  background: ${T.amberBg}; color: ${T.amber};
  border: 1px solid ${T.amberBorder};
  flex-shrink: 0;
}
.gw-rel-actions { display: flex; gap: 4px; flex-shrink: 0; }
.gw-rel-icon-btn {
  all: unset;
  width: 24px; height: 24px;
  border-radius: 5px;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 13px;
  color: ${T.textMuted}; transition: all .12s;
}
.gw-rel-icon-btn:hover { background: ${T.bgSidebar}; color: ${T.brand}; }
.gw-rel-icon-btn.danger:hover { background: ${T.redBg}; color: ${T.red}; }
.gw-rel-suggest-banner {
  padding: 10px 16px; background: ${T.amberBg};
  border-bottom: 1px solid ${T.amberBorder};
  font-size: 11.5px; color: ${T.amber};
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}

/* ── Catalog browser ── */
.gw-catalog-browse {
  display: grid;
  grid-template-columns: 220px 220px 1fr;
  gap: 0;
  height: 400px;
  border: 1px solid ${T.border};
  border-radius: 10px; overflow: hidden;
  background: ${T.bgCard};
}
.gw-catalog-pane {
  display: flex; flex-direction: column;
  border-right: 1px solid ${T.border};
  overflow: hidden;
  background: ${T.bgCard};
}
.gw-catalog-pane:last-child { border-right: none; }
.gw-catalog-pane-hdr {
  padding: 9px 12px; background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 600;
  letter-spacing: .14em; text-transform: uppercase;
  color: ${T.textMuted};
}
.gw-catalog-pane-body { flex: 1; overflow-y: auto; padding: 7px; }
.gw-catalog-item {
  padding: 8px 10px; border-radius: 6px;
  cursor: pointer; font-size: 12px; color: ${T.textPrimary};
  display: flex; flex-direction: column; gap: 2px;
  transition: background .12s;
}
.gw-catalog-item:hover { background: ${T.brandLight}; }
.gw-catalog-item.active { background: ${T.brand}; color: #fff; }
.gw-catalog-item-name { font-weight: 600; font-size: 12px; }
.gw-catalog-item-desc {
  font-size: 10.5px; color: ${T.textMuted};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gw-catalog-item.active .gw-catalog-item-desc { color: rgba(255,255,255,.85); }

/* ── Footer (back / continue) ── */
.gw-wizard-footer {
  position: sticky;
  bottom: 0;
  padding: 16px 36px;
  background: ${T.bgCard};
  border-top: 1px solid ${T.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  z-index: 5;
  box-shadow: 0 -2px 10px rgba(0,0,0,.03);
}
.gw-wizard-footer-msg {
  font-size: 13px;
  color: ${T.textMuted};
  flex: 1;
  min-width: 0;
  line-height: 1.4;
}
.gw-wizard-footer-msg.error { color: ${T.red}; font-weight: 500; }
.gw-wizard-footer-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}
.gw-wizard-footer-actions .gw-btn {
  padding: 9px 18px;
  font-size: 13px;
  min-width: 100px;
  justify-content: center;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTEXT 2 — Embedded inside an existing panel (.gw-wizard-embedded)
   ═══════════════════════════════════════════════════════════════════════════ */
.gw-wizard-embedded {
  background: transparent;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.gw-wizard-embedded .gw-wizard-stepper-only {
  padding: 0 0 18px;
  margin-bottom: 22px;
  border-bottom: 1px solid ${T.border};
  flex-shrink: 0;
}
.gw-wizard-embedded .gw-wizard-body {
  padding: 0;
  overflow: visible;
  flex: 1 1 auto;
  min-height: 0;
}
.gw-wizard-embedded .gw-wizard-narrow {
  max-width: 100%;
  margin: 0;
}
.gw-wizard-embedded .gw-section-hdr {
  margin-top: 0;
  margin-bottom: 18px;
}
.gw-wizard-embedded .gw-wizard-footer {
  position: static;
  padding: 18px 0 0;
  margin-top: 24px;
  background: transparent;
  border-top: 1px solid ${T.border};
  box-shadow: none;
  flex-shrink: 0;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTEXT 3 — Inside a Bootstrap Modal body (.gw-wizard-modal)
   The modal body has its own scroll, so we don't double-scroll.
   ═══════════════════════════════════════════════════════════════════════════ */
.gw-wizard-modal {
  background: transparent;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.gw-wizard-modal .gw-wizard-stepper-only {
  padding: 16px 24px 18px;
  margin-bottom: 0;
  background: ${T.bgSidebar};
  border-bottom: 1px solid ${T.border};
}
.gw-wizard-modal .gw-wizard-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}
.gw-wizard-modal .gw-wizard-narrow {
  max-width: 100%;
  margin: 0;
}
.gw-wizard-modal .gw-wizard-footer {
  position: sticky;
  bottom: 0;
  padding: 14px 28px;
  background: ${T.bgCard};
  border-top: 1px solid ${T.border};
}


/* ═══════════════════════════════════════════════════════════════════════════
   DQ Workspace additions — rule rows, lifecycle, panels, dashboards,
   collapsible sections, sparklines, token map editor, rule details modal.
   All classes referenced by:
     - DMTreeGridSummary.tsx
     - CleansingPatternDashboard.tsx
     - CollapsibleSection.tsx
     - TokenMapEditor.tsx
     - DQRuleDetailsModal.tsx
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Status pill (DQDashboard filter strip) ── */
.gw-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 20px;
  font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .15s;
  border: 1.5px solid transparent;
  user-select: none;
  font-family: 'Sora', system-ui, sans-serif;
}
.gw-pill.active { color: #fff; }
.gw-pill.inactive {
  background: var(--gw-bg-card);
  color: var(--gw-text-muted);
  border-color: var(--gw-border);
}
.gw-pill.inactive:hover {
  border-color: var(--gw-brand-mid);
  color: var(--gw-brand);
}

/* ── Rule row ── */
.gw-rule-row {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 8px 10px; border-radius: 8px;
  transition: background .12s;
}
.gw-rule-row:hover { background: var(--gw-bg-sidebar); }

/* ── Lifecycle badge ── */
.gw-lc-badge {
  font-size: 10px; font-weight: 700;
  padding: 2px 8px; border-radius: 10px;
  text-transform: uppercase; letter-spacing: .04em;
  display: inline-block;
}
.gw-lc-Approved { background: var(--gw-green-bg); color: var(--gw-green); border: 1px solid var(--gw-green-bd); }
.gw-lc-Draft    { background: var(--gw-bg-sidebar); color: var(--gw-text-muted); border: 1px solid var(--gw-border); }
.gw-lc-Rejected { background: var(--gw-red-bg);   color: var(--gw-red);   border: 1px solid var(--gw-red-bd); }
.gw-lc-Waiting  { background: var(--gw-amber-bg); color: var(--gw-amber); border: 1px solid var(--gw-amber-bd); }

/* ── Add-rule pill button ── */
.gw-add-rule-btn {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600;
  color: var(--gw-brand);
  background: var(--gw-brand-light);
  border: 1.5px solid var(--gw-brand-mid);
  border-radius: 20px;
  padding: 4px 12px;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
  font-family: 'Sora', system-ui, sans-serif;
}
.gw-add-rule-btn:hover {
  background: var(--gw-brand);
  color: #fff;
  border-color: var(--gw-brand);
}

/* ── Group header (data tree) ── */
.gw-group-hdr { background: var(--gw-bg-sidebar); }
.gw-group-hdr td { padding: 10px 16px; }
.gw-group-toggle {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 700;
  color: var(--gw-brand);
  cursor: pointer; border: none; background: none; padding: 0;
  font-family: 'Sora', system-ui, sans-serif;
}

/* ── Section header inside rules pane ── */
.gw-rules-cat-hdr {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .08em;
  color: var(--gw-text-muted);
  padding: 4px 0 6px;
}

/* ── Stat label below sparklines ── */
.gw-stat-label {
  font-size: 10px; font-weight: 700;
  color: var(--gw-text-muted);
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-top: 4px;
}

/* ── Sparkline vertical bars ── */
.gw-vbar-wrap {
  display: flex; align-items: flex-end;
  gap: 3px; height: 52px;
}
.gw-vbar-item {
  width: 9px;
  border-radius: 2px 2px 0 0;
  min-height: 2px;
}

/* ── Collapsible section (Cleansing/Policy dashboards) ── */
.gw-collapsible {
  background: var(--gw-bg-card);
  border: 1px solid var(--gw-border);
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
  border-left: 1px solid var(--gw-border);
}
.gw-collapsible.has-active { border-left: 3px solid var(--gw-brand); }
.gw-collapsible-hdr {
  width: 100%;
  display: flex; align-items: center; gap: 10px;
  padding: 8px 14px;
  background: transparent;
  border: none; cursor: pointer; text-align: left;
  transition: background .15s;
  font-family: 'Sora', system-ui, sans-serif;
}
.gw-collapsible-hdr.open  { background: var(--gw-bg-sidebar); }
.gw-collapsible-hdr:hover { background: var(--gw-bg-sidebar); }
.gw-collapsible-icon {
  width: 22px; height: 22px;
  border-radius: 5px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.gw-collapsible-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px; font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--gw-text);
  white-space: nowrap;
}
.gw-collapsible-summary {
  flex: 1; min-width: 0;
  font-size: 11px; color: var(--gw-text-muted);
  display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
}
.gw-collapsible-body {
  padding: 12px 14px;
  border-top: 1px solid var(--gw-border);
  background: var(--gw-bg-card);
}

/* ── Cleansing dashboard panel shell ── */
.gw-panel {
  background: var(--gw-bg-card);
  border: 1px solid var(--gw-border);
  border-radius: 10px;
  padding: 12px 14px;
}
.gw-panel-hdr {
  display: flex; align-items: center; gap: 7px;
  margin-bottom: 10px;
}
.gw-panel-hdr-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--gw-text-muted);
}
.gw-panel-hdr-sub {
  font-size: 10px;
  color: var(--gw-text-muted);
  margin-bottom: 10px;
}

/* ── Hero gradient icon block ── */
.gw-hero-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--gw-brand), var(--gw-purple));
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* ───────────────────────────────────────────────────────────────────────────
   Token Map Editor
   ─────────────────────────────────────────────────────────────────────────── */

.gw-tme {
  border: 1px solid var(--gw-border);
  border-radius: 12px;
  background: var(--gw-bg-card);
  overflow: hidden;
}
.gw-tme-hdr {
  padding: 12px 16px;
  border-bottom: 1px solid var(--gw-border);
  background: var(--gw-bg-sidebar);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.gw-tme-hdr-left {
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
}
.gw-tme-body { padding: 16px; }
.gw-tme-foot {
  display: flex; gap: 8px; justify-content: flex-end;
  padding: 10px 16px;
  border-top: 1px solid var(--gw-border);
  background: var(--gw-bg-sidebar);
}
.gw-tme-error {
  padding: 8px 16px;
  background: var(--gw-red-bg);
  border: 1px solid var(--gw-red-bd);
  color: var(--gw-red);
  font-size: 12px;
}
.gw-tme-loading {
  padding: 30px;
  text-align: center;
  color: var(--gw-text-muted);
  font-size: 13px;
}
.gw-tme-warning {
  padding: 14px 16px;
  background: var(--gw-amber-bg);
  border: 1px solid var(--gw-amber-bd);
  border-radius: 10px;
  color: var(--gw-amber);
  font-size: 12px;
}

/* ── Mapping editor ── */
.gw-mapping-list {
  border: 1px solid var(--gw-border);
  border-radius: 8px;
  max-height: 360px;
  overflow-y: auto;
}
.gw-mapping-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--gw-border);
  background: var(--gw-bg-card);
}
.gw-mapping-row.identity { background: var(--gw-bg-sidebar); }
.gw-mapping-row:last-child { border-bottom: none; }
.gw-mapping-empty {
  padding: 24px;
  text-align: center;
  color: var(--gw-text-muted);
  font-size: 12px;
}
.gw-mapping-add-row {
  display: flex; gap: 8px; align-items: center;
  padding: 10px 12px; margin-bottom: 12px;
  background: var(--gw-brand-light);
  border: 1px dashed var(--gw-brand);
  border-radius: 8px;
}

/* ── Regex pair card ── */
.gw-pair-card {
  border: 1px solid var(--gw-border);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  background: var(--gw-bg-card);
}
.gw-pair-card-hdr {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 12px;
}
.gw-pair-step-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  color: var(--gw-text-muted);
}
.gw-pair-kind-chip {
  font-size: 10px; font-weight: 700;
  padding: 2px 9px; border-radius: 9px;
}
.gw-pair-kind-preset        { background: var(--gw-green-bg);    color: var(--gw-green); }
.gw-pair-kind-literal_list  { background: var(--gw-brand-light); color: var(--gw-brand); }
.gw-pair-kind-synonym_group { background: var(--gw-purple-bg);   color: var(--gw-purple); }
.gw-pair-kind-reformat      { background: var(--gw-amber-bg);    color: var(--gw-amber); }
.gw-pair-kind-regex_raw     { background: var(--gw-red-bg);      color: var(--gw-red); }

/* ── Token chips (literal-list editor) ── */
.gw-token-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: 'JetBrains Mono', monospace; font-size: 11px;
  padding: 3px 8px; border-radius: 12px;
  background: var(--gw-brand-light);
  color: var(--gw-brand);
  border: 1px solid var(--gw-brand-mid);
}
.gw-token-chip-input {
  flex: 1; min-width: 100px;
  border: none; outline: none;
  font-family: 'JetBrains Mono', monospace; font-size: 11px;
  background: transparent;
}
.gw-token-chip-area {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 10px;
  border: 1px solid var(--gw-border);
  border-radius: 8px;
  background: var(--gw-bg-sidebar);
  min-height: 40px;
  margin-bottom: 10px;
}

/* ── Read-only callout (reformat / regex_raw editors) ── */
.gw-readonly-box {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px; padding: 10px;
  background: var(--gw-bg-sidebar);
  border: 1px solid var(--gw-border);
  border-radius: 6px;
  color: var(--gw-text);
  line-height: 1.7;
}

/* ── Inline notice banners ── */
.gw-notice {
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 11px;
  margin-bottom: 10px;
  display: flex; align-items: flex-start; gap: 6px;
}
.gw-notice-info {
  background: var(--gw-amber-bg);
  border: 1px solid var(--gw-amber-bd);
  color: var(--gw-amber);
}
.gw-notice-advanced {
  background: var(--gw-purple-bg);
  border: 1px solid var(--gw-purple-bd);
  color: var(--gw-purple);
}

/* ── Preview tester ── */
.gw-tester-divider {
  margin-top: 14px; padding-top: 12px;
  border-top: 1px dashed var(--gw-border);
}
.gw-tester-results {
  margin-top: 8px;
  border: 1px solid var(--gw-border);
  border-radius: 6px;
  max-height: 180px;
  overflow-y: auto;
}
.gw-tester-row {
  display: grid; grid-template-columns: 1fr auto 1fr;
  gap: 10px; padding: 5px 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  border-bottom: 1px solid var(--gw-border);
}
.gw-tester-row:last-child { border-bottom: none; }
.gw-tester-row.changed   { background: var(--gw-green-bg); }
.gw-tester-row.unchanged { background: var(--gw-bg-sidebar); }

/* ── Inline input style (token map cards) ── */
.gw-inline-input {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  padding: 5px 10px;
  border: 1px solid var(--gw-border);
  border-radius: 6px;
  background: var(--gw-bg-card);
  outline: none;
  flex: 1; min-width: 0;
}

/* ───────────────────────────────────────────────────────────────────────────
   Rule Details Modal
   ─────────────────────────────────────────────────────────────────────────── */

.gw-rdm-hdr {
  background: var(--gw-brand);
  padding: 18px 24px 16px;
  border-radius: 12px 12px 0 0;
  display: flex; justify-content: space-between; align-items: flex-start;
  color: #fff;
}
.gw-rdm-hdr-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: rgba(255,255,255,.5);
}
.gw-rdm-hdr-id {
  font-family: 'JetBrains Mono', monospace;
  font-size: 20px; font-weight: 700;
  color: #fff;
  letter-spacing: -.01em;
}
.gw-rdm-hdr-desc {
  font-size: 13px;
  color: rgba(255,255,255,.7);
  margin-top: 3px;
  max-width: 500px;
}
.gw-rdm-hdr-policy-pill {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  background: rgba(255,255,255,.18);
  color: #fff;
  border: 1px solid rgba(255,255,255,.35);
  border-radius: 8px;
  padding: 2px 8px;
  letter-spacing: .07em;
}
.gw-rdm-hdr-close {
  background: rgba(255,255,255,.15);
  border: none;
  color: #fff;
  border-radius: 8px;
  width: 32px; height: 32px;
  cursor: pointer;
  font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}

.gw-rdm-status-bar {
  display: flex;
  background: var(--gw-bg-sidebar);
  border-bottom: 1px solid var(--gw-border);
}
.gw-rdm-status-cell {
  flex: 1;
  padding: 10px 16px;
  border-right: 1px solid var(--gw-border);
}
.gw-rdm-status-cell:last-child { border-right: none; }
.gw-rdm-status-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: var(--gw-text-muted);
  margin-bottom: 3px;
}
.gw-rdm-status-value {
  font-size: 13px;
  font-weight: 700;
}

.gw-rdm-body {
  padding: 24px;
  overflow-y: auto;
  max-height: 62vh;
  background: var(--gw-bg-card);
}
.gw-rdm-foot {
  padding: 14px 24px;
  background: var(--gw-bg-sidebar);
  border-top: 1px solid var(--gw-border);
  border-radius: 0 0 12px 12px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* ── Section in modal body ── */
.gw-rdm-section { margin-bottom: 24px; }
.gw-rdm-section-hdr {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--gw-border);
}
.gw-rdm-section-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: var(--gw-text-muted);
}

/* ── Stat boxes (execution summary) ── */
.gw-rdm-stat {
  background: var(--gw-bg-sidebar);
  border: 1px solid var(--gw-border);
  border-radius: 10px;
  padding: 10px 14px;
}
.gw-rdm-stat.danger {
  background: var(--gw-red-bg);
  border-color: var(--gw-red-bd);
}
.gw-rdm-stat-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  margin-bottom: 4px;
}
.gw-rdm-stat-value {
  font-size: 18px;
  font-weight: 700;
}

/* ── Definition row (label + value) ── */
.gw-rdm-defrow {
  display: flex; gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--gw-border);
}
.gw-rdm-defrow:last-child { border-bottom: none; }
.gw-rdm-defrow-label {
  min-width: 148px;
  font-size: 12px;
  font-weight: 700;
  color: var(--gw-text-muted);
}
.gw-rdm-defrow-value {
  font-size: 13px;
  color: var(--gw-text);
  flex: 1;
}

/* ── Policy doc card ── */
.gw-policy-doc-card {
  background: var(--gw-brand-light);
  border: 1px solid var(--gw-brand-mid);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  display: flex; align-items: flex-start; gap: 12px;
}
.gw-policy-doc-icon-wrap {
  width: 36px; height: 36px;
  border-radius: 8px;
  background: var(--gw-brand);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.gw-policy-doc-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--gw-brand);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gw-policy-doc-page-pill {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px; font-weight: 700;
  color: var(--gw-brand);
  background: rgba(30,58,110,.12);
  border-radius: 6px;
  padding: 1px 7px;
}
.gw-policy-doc-section-pill {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px; font-weight: 700;
  color: var(--gw-text-muted);
  background: var(--gw-bg-card);
  border: 1px solid var(--gw-border);
  border-radius: 6px;
  padding: 1px 7px;
}

/* ── Confidence bar (modal) ── */
.gw-conf-bar-wrap {
  flex: 1;
  height: 6px;
  background: var(--gw-bg-sidebar);
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid var(--gw-border);
}
.gw-conf-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width .4s ease;
}

/* ── Code block ── */
.gw-code {
  background: var(--gw-bg-sidebar);
  border: 1px solid var(--gw-border);
  border-radius: 8px;
  padding: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--gw-text);
  overflow-x: auto;
  margin: 0;
  white-space: pre-wrap;
}
.gw-code-danger {
  background: var(--gw-red-bg);
  border-color: var(--gw-red-bd);
  color: var(--gw-text);
  max-height: 200px;
  overflow-y: auto;
}

/* ── Risk box (policy panel) ── */
.gw-risk-box {
  background: var(--gw-red-bg);
  border: 1px solid var(--gw-red-bd);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: var(--gw-red);
  line-height: 1.6;
}

`;

// ─── Injection helpers ────────────────────────────────────────────────────────

/* SMART SCROLLBARS: thin and invisible until the user scrolls; shown (Accenture purple) while scrolling and for a
   second after. Scrolling with the wheel, keyboard or by dragging works as before. */
const SMART_SCROLL_CSS = `
/* LIGHT APP HEADER: white bar, the purple gradient line at the bottom, a soft shadow; buttons / avatar stay purple */
.gw-app-header { background: #ffffff linear-gradient(90deg, #460073 0%, #7500C0 50%, #A100FF 100%) no-repeat left bottom / 100% 3px !important;
  box-shadow: 0 1px 10px rgba(70,0,115,.08) !important; color: #1f2937; }
.gw-app-header .gw-app-header-wordmark { color: #1f2937 !important; }
.gw-app-header .gw-app-header-sub { color: #6b7280 !important; }
.gw-app-header .gw-app-header-divider { background: #e5e7eb !important; }
.gw-app-header .gw-btn-ghost, .gw-app-header .gw-btn-outline { color: #460073 !important; border-color: #d9c2f5 !important; background: #ffffff !important; }
.gw-app-header .gw-btn-ghost:hover, .gw-app-header .gw-btn-outline:hover { border-color: #A100FF !important; background: #faf6ff !important; color: #460073 !important; }
.gw-app-header .gw-user-menu-trigger { color: #1f2937 !important; border-color: #e3d0fb !important; background: #ffffff !important; }
.gw-app-header .gw-user-menu-trigger:hover { border-color: #A100FF !important; background: #faf6ff !important; }
.gw-app-header .gw-user-menu-trigger * { color: inherit; }
.gw-app-header .gw-user-avatar { color: #ffffff !important; }
.gw-app-header .gw-app-header-divider + * { color: #33424e !important; }
* { scrollbar-width: thin; scrollbar-color: transparent transparent !important; }
.gw-scrolling { scrollbar-color: rgba(117,0,192,.45) transparent !important; }
::-webkit-scrollbar { width: 7px; height: 7px; }
::-webkit-scrollbar-track { background: transparent !important; }
::-webkit-scrollbar-thumb { background: transparent !important; border-radius: 4px; }
.gw-scrolling::-webkit-scrollbar-thumb { background: rgba(117,0,192,.45) !important; }
`;
function installSmartScroll(): void {
  const w = window as any;
  if (w.__gwSmartScroll) return;
  w.__gwSmartScroll = true;
  document.addEventListener('scroll', (e: Event) => {
    const t = e.target as any;
    const el: any = t === document || !t || !t.classList ? document.documentElement : t;
    el.classList.add('gw-scrolling');
    clearTimeout(el.__gwT);
    el.__gwT = setTimeout(() => el.classList.remove('gw-scrolling'), 1000);
  }, true);                                   // capture: scroll events do not bubble, so listen on the way down
}

export function injectTheme(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(THEME_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = THEME_CSS_ID;
  style.textContent = THEME_CSS + SMART_SCROLL_CSS;
  document.head.appendChild(style);
  installSmartScroll();
}

export function injectLandingTheme(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(LANDING_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = LANDING_CSS_ID;
  style.textContent = LANDING_CSS;
  document.head.appendChild(style);
}

// ─── React-Select style overrides ────────────────────────────────────────────
export const selectStyles = {
  control: (b: any, s: any) => ({
    ...b,
    borderRadius: 8,
    border: `1px solid ${s.isFocused ? T.brandMid : T.border}`,
    background: T.bgSidebar,
    minHeight: 36,
    boxShadow: s.isFocused ? `0 0 0 3px rgba(30,58,110,.07)` : 'none',
    '&:hover': { borderColor: T.brandMid },
  }),
  menu: (b: any) => ({
    ...b, borderRadius: 8, border: `1px solid ${T.border}`,
    boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 9999,
  }),
  option: (b: any, s: any) => ({
    ...b, fontSize: 12,
    background: s.isSelected ? T.brand : s.isFocused ? T.brandLight : '#fff',
    color: s.isSelected ? '#fff' : T.textPrimary,
  }),
  multiValue:       (b: any) => ({ ...b, background: T.brandLight, borderRadius: 6 }),
  multiValueLabel:  (b: any) => ({ ...b, color: T.brand, fontSize: 11, fontWeight: 600 }),
  multiValueRemove: (b: any) => ({ ...b, color: T.brandMid, ':hover': { background: T.brand, color: '#fff' } }),
  placeholder:      (b: any) => ({ ...b, fontSize: 12, color: T.textMuted }),
  input:            (b: any) => ({ ...b, fontSize: 12, fontFamily: "'Sora', system-ui, sans-serif" }),
  groupHeading:     (b: any) => ({
    ...b, fontWeight: 700, fontSize: '0.85em', color: T.textLabel,
    paddingTop: 6, paddingBottom: 4, borderBottom: `1px solid ${T.border}`,
  }),
};